import cn from 'classnames';
import type { MouseEventHandler, RefObject } from 'react';
import React, { memo, useCallback, useContext } from 'react';

import { Button } from '@sravni/react-design-system';
import { PartnerBadge } from '@sravni/react-ugc/lib/PartnerBadge';
import { COMMON_OFFERS } from '@sravni/ugc/lib/specialOffers/lists';
import type { CommonOffersType, Tab } from '@sravni/ugc/lib/specialOffers/types';

import { SpecialOffersContext } from '../../../../../contexts/OffersPageQueryContext';
import type { EventPayload } from '../../../../../helpers/sendMessageToParentWindow';
import { useOfferData } from '../../../../../hooks/useOfferData';
import type { EnrichedOfferFromBff } from '../../../../../interfaces/offers';
import { useActiveTabType } from '../../../../../store/hooks/offers';
import styles from '../../../block.module.scss';
import { useViewObserver } from '../../../hooks/useViewObserver';

import { MainBlock } from './components/MainBlock';
import { Logo } from './Logo';

export interface IOfferBlockProps {
  blockRef: RefObject<HTMLAnchorElement>;
  isMobile: boolean;
  handleEvent: (event: EventPayload) => void;

  item: EnrichedOfferFromBff;

  isPopup: boolean;
}

export const OfferBlock = memo<IOfferBlockProps>(({ item, blockRef, isMobile, handleEvent, isPopup }) => {
  const activeTabType = useActiveTabType() as Tab<CommonOffersType>;

  const {
    bankName,
    logoSrc,
    isCard,
    offerTitle,
    rating,
    reviewCount,
    conditions,
    isPartnerCard,
    organization,
    noticeAboutAdvertisement,
    buttonLabel,
    labelFromAdvertising,
    clickBlockEvent,
    viewBlockEvent,
    offerTitleClickEvent,
    buttonClickEvent,
    clickLink,
    sendViewingEventViaPixel,
  } = useOfferData({ tab: activeTabType, item, isPopup });

  const handleBlockClick = useCallback(() => handleEvent(clickBlockEvent), [clickBlockEvent, handleEvent]);

  const handleView = useCallback(() => {
    handleEvent(viewBlockEvent);
    sendViewingEventViaPixel();
  }, [handleEvent, sendViewingEventViaPixel, viewBlockEvent]);

  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      if (buttonClickEvent) {
        event.stopPropagation();
        handleEvent(buttonClickEvent);
      }
    },
    [buttonClickEvent, handleEvent],
  );

  const { isWithButtons } = useContext(SpecialOffersContext);

  useViewObserver({
    handleView,
    blockRef,
  });

  const logo = <Logo logoSrc={logoSrc} isCard={isCard} tabType={activeTabType} />;
  const logoBlock = isPartnerCard ? <PartnerBadge>{logo}</PartnerBadge> : logo;

  const handleOfferTitleClick = useCallback(() => {
    handleEvent(offerTitleClickEvent);
  }, [handleEvent, offerTitleClickEvent]);

  return (
    <a
      className={cn(styles.block, styles['block--offer'], {
        [styles['block--loan']]: activeTabType.type === COMMON_OFFERS.loan,
      })}
      href={clickLink}
      onClick={handleBlockClick}
      target="_blank"
      rel="noreferrer"
      ref={blockRef}
    >
      <MainBlock
        activeTabType={activeTabType}
        bankName={bankName}
        logoBlock={logoBlock}
        conditions={conditions}
        labelFromAdvertising={labelFromAdvertising}
        organization={organization}
        rating={rating}
        reviewCount={reviewCount}
        isMobile={isMobile}
        item={item}
        isWithButtons={isWithButtons}
        offerTitle={offerTitle}
        handleOfferTitleClick={handleOfferTitleClick}
      />
      {isWithButtons && (
        <Button
          size={36}
          variant="primary"
          block
          className={cn(styles.button, { [styles['button--withNoticeAboutAdv']]: !!noticeAboutAdvertisement })}
          onClick={handleButtonClick}
        >
          {buttonLabel}
        </Button>
      )}
      {noticeAboutAdvertisement && <div className={styles.noticeAboutAdvertisement}>{noticeAboutAdvertisement}</div>}
    </a>
  );
});
